import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Printer, RefreshCw, Sun, Tent, Music, Trophy, Clock, Save, Trash2, Plus, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function ManualEntriesPanel({ date, headers }) {
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState(["Main Tent", "Hockey Rink", "Auditorium"]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState("Main Tent");
  const [busy, setBusy] = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dismissal/manual-entries`, { headers, params: { date } });
      setEntries(res.data.entries || []);
      if (res.data.locations?.length) setLocations(res.data.locations);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const addEntry = async () => {
    if (!name.trim()) { toast.error("Enter a name"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/dismissal/manual-entries`,
        { name: name.trim(), location, date, note: note.trim() }, { headers });
      toast.success(`${name.trim()} added to the ${location} list`);
      setName(""); setNote("");
      loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const removeEntry = async (entry) => {
    try {
      await axios.delete(`${API}/admin/dismissal/manual-entries/${entry.id}`, { headers });
      toast.success(`${entry.name} removed`);
      loadEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <Card className="border-violet-200" data-testid="manual-dismissal-panel">
      <div className="h-1.5 bg-violet-500 rounded-t-lg" />
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-violet-700" />
          <h3 className="font-bold text-sm text-violet-800">Manually Add a Name to a List</h3>
          <span className="text-[11px] text-muted-foreground">printed pickup lists only — never affects attendance or banners</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="px-2 py-1.5 text-sm border rounded-md bg-white flex-1 min-w-[140px]"
            data-testid="manual-entry-name-input"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note / group (optional)"
            className="px-2 py-1.5 text-sm border rounded-md bg-white flex-1 min-w-[120px]"
            data-testid="manual-entry-note-input"
          />
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded-md bg-white"
            data-testid="manual-entry-location-select"
          >
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Button size="sm" onClick={addEntry} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="manual-entry-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add
          </Button>
        </div>
        {entries.length > 0 && (
          <div className="space-y-1.5">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-sm bg-violet-50 border border-violet-100 rounded-md px-2.5 py-1.5" data-testid={`manual-entry-row-${e.id}`}>
                <span className="font-semibold">{e.name}</span>
                {e.note && <span className="text-xs text-muted-foreground">({e.note})</span>}
                <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]">{e.location}</Badge>
                <button
                  onClick={() => removeEntry(e)}
                  className="ml-auto text-red-400 hover:text-red-600 p-0.5"
                  title={`Remove ${e.name}`}
                  data-testid={`manual-entry-remove-${e.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DismissalReportsView({ onBack }) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const [summary, setSummary] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(() => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }));
  const [selectedHockeyGroups, setSelectedHockeyGroups] = useState([]);
  const [newReportName, setNewReportName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [downloading, setDownloading] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, savedRes] = await Promise.all([
        axios.get(`${API}/admin/dismissal/summary`, { headers, params: { date } }),
        axios.get(`${API}/admin/dismissal/hockey-rink/saved`, { headers }),
      ]);
      setSummary(sumRes.data);
      setSavedReports(savedRes.data || []);
    } catch {
      toast.error("Failed to load dismissal data");
    } finally {
      setLoading(false);
    }
  }, [date, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds so early pickups update live
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const downloadPdf = async (slug, params = {}, niceName = "report", lockKey = "") => {
    const key = lockKey || slug;
    setDownloading(key);
    try {
      const res = await axios.get(`${API}/admin/dismissal/${slug}`, {
        headers,
        params: { date, ...params },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) {
        w.addEventListener("load", () => { try { w.print(); } catch {} });
      }
      toast.success(`${niceName} PDF generated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate PDF");
    } finally {
      setDownloading("");
    }
  };

  const handleSaveReport = async () => {
    if (!newReportName.trim()) { toast.error("Report name is required"); return; }
    if (selectedHockeyGroups.length === 0) { toast.error("Select at least one group"); return; }
    setCreating(true);
    try {
      await axios.post(
        `${API}/admin/dismissal/hockey-rink/saved`,
        { name: newReportName.trim(), groups: selectedHockeyGroups },
        { headers }
      );
      toast.success(`Saved report "${newReportName.trim()}"`);
      setNewReportName("");
      setSelectedHockeyGroups([]);
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete saved report "${r.name}"? The groups will become available again.`)) return;
    try {
      await axios.delete(`${API}/admin/dismissal/hockey-rink/saved/${r.id}`, { headers });
      toast.success(`Deleted "${r.name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const locs = summary?.locations || {};
  const mainTent = locs["Main Tent"] || { total: 0, by_group: {} };
  const auditorium = locs["Auditorium"] || { total: 0, by_group: {} };
  const hockey = locs["Hockey Rink"] || { total: 0, by_group: {} };
  const classroom = locs["Classroom"] || { total: 0, by_group: {} };
  const hockeyGroups = Object.keys(hockey.by_group || {}).sort();
  const classroomGroups = Object.keys(classroom.by_group || {}).sort();

  const toggleGroup = (g) => {
    setSelectedHockeyGroups((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };
  const selectAllHockey = () => setSelectedHockeyGroups(hockeyGroups);
  const clearHockey = () => setSelectedHockeyGroups([]);

  return (
    <div className="space-y-4" data-testid="dismissal-reports-view">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="dismissal-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <Sun className="w-5 h-5 text-amber-600" />End-of-Day Dismissal Reports
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Date:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-2 py-1 text-sm border rounded-md bg-white"
            data-testid="dismissal-date"
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="dismissal-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-amber-50/40 border-amber-200">
        <CardContent className="p-3 flex items-center gap-2 text-sm text-amber-900">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>
            Live counts auto-refresh every 30 seconds. Campers marked as
            "Dismissed" (early pickup) are <strong>automatically removed</strong> from the printed list.
          </span>
        </CardContent>
      </Card>

      <ManualEntriesPanel date={date} headers={headers} />

      <div className="grid gap-4 md:grid-cols-2">
        {/* Main Tent */}
        <Card data-testid="dismissal-main-tent-card" className="border-blue-200">
          <div className="h-1.5 bg-blue-500 rounded-t-lg" />
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tent className="w-5 h-5 text-blue-700" />
                <h3 className="font-bold text-base text-blue-800">Main Tent</h3>
              </div>
              <Badge className="bg-blue-100 text-blue-800 border-blue-200">{mainTent.total} kids</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              All campers heading to the Main Tent for car pickup or after-care handoff.
            </p>
            <Button
              className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={downloading === "main-tent.pdf" || mainTent.total === 0}
              onClick={() => downloadPdf("main-tent.pdf", { staff_only: false, cit_only: false }, "Main Tent")}
              data-testid="dismissal-print-main-tent"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "main-tent.pdf" ? "Generating..." : "Print Main Tent List"}
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              disabled={downloading === "main-tent.pdf:staff" || mainTent.total === 0}
              onClick={() => downloadPdf("main-tent.pdf", { staff_only: true, cit_only: false }, "Main Tent Staff Kids", "main-tent.pdf:staff")}
              data-testid="dismissal-print-main-tent-staff"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "main-tent.pdf:staff" ? "Generating..." : "Print Staff Kids — Main Tent"}
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              disabled={downloading === "main-tent.pdf:cit" || mainTent.total === 0}
              onClick={() => downloadPdf("main-tent.pdf", { staff_only: false, cit_only: true }, "Main Tent CITs", "main-tent.pdf:cit")}
              data-testid="dismissal-print-main-tent-cit"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "main-tent.pdf:cit" ? "Generating..." : "Print CITs — Main Tent"}
            </Button>
          </CardContent>
        </Card>

        {/* Auditorium */}
        <Card data-testid="dismissal-auditorium-card" className="border-purple-200">
          <div className="h-1.5 bg-purple-500 rounded-t-lg" />
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Music className="w-5 h-5 text-purple-700" />
                <h3 className="font-bold text-base text-purple-800">Auditorium</h3>
              </div>
              <Badge className="bg-purple-100 text-purple-800 border-purple-200">{auditorium.total} kids</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              All campers heading to the Auditorium for dismissal.
            </p>
            <Button
              className="w-full mt-3 bg-purple-600 hover:bg-purple-700 text-white"
              disabled={downloading === "auditorium.pdf" || auditorium.total === 0}
              onClick={() => downloadPdf("auditorium.pdf", { staff_only: false }, "Auditorium")}
              data-testid="dismissal-print-auditorium"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "auditorium.pdf" ? "Generating..." : "Print Auditorium List"}
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              disabled={downloading === "auditorium.pdf:staff" || auditorium.total === 0}
              onClick={() => downloadPdf("auditorium.pdf", { staff_only: true }, "Auditorium Staff Kids", "auditorium.pdf:staff")}
              data-testid="dismissal-print-auditorium-staff"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "auditorium.pdf:staff" ? "Generating..." : "Print Staff Kids — Auditorium"}
            </Button>
          </CardContent>
        </Card>

        {/* Classroom (PM exception) */}
        <Card data-testid="dismissal-classroom-card" className="border-amber-300">
          <div className="h-1.5 bg-amber-500 rounded-t-lg" />
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-700" />
                <h3 className="font-bold text-base text-amber-800">Classroom</h3>
              </div>
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">{classroom.total} kids</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Kids switched to Classroom pickup today (PM exception). Two printouts: one combined list with siblings on the same line, or one page per group.
            </p>
            {classroomGroups.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {classroomGroups.map((g) => (
                  <Badge key={g} variant="outline" className="text-[10px] border-amber-300 text-amber-700" data-testid={`classroom-group-${g}`}>
                    {g} · {classroom.by_group[g]}
                  </Badge>
                ))}
              </div>
            )}
            <Button
              className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white"
              disabled={downloading === "classroom.pdf" || classroom.total === 0}
              onClick={() => downloadPdf("classroom.pdf", {}, "Classroom")}
              data-testid="dismissal-print-classroom"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "classroom.pdf" ? "Generating..." : "Full List — Siblings Together"}
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2 border-amber-400 text-amber-700 hover:bg-amber-50"
              disabled={downloading === "classroom-by-group.pdf" || classroom.total === 0}
              onClick={() => downloadPdf("classroom-by-group.pdf", {}, "Classroom by Group")}
              data-testid="dismissal-print-classroom-bygroup"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading === "classroom-by-group.pdf" ? "Generating..." : "By Group — Page Per Group"}
            </Button>
            {classroom.total === 0 && (
              <p className="text-[11px] text-muted-foreground mt-2 italic">
                No kids marked Classroom for this date — set a PM exception with location "Classroom" to add them.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hockey Rink */}
      <Card data-testid="dismissal-hockey-card" className="border-green-200">
        <div className="h-1.5 bg-green-500 rounded-t-lg" />
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-green-700" />
              <h3 className="font-bold text-base text-green-800">Hockey Rink</h3>
            </div>
            <Badge className="bg-green-100 text-green-800 border-green-200">{hockey.total} kids total</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            Create saved reports once. After saving, those groups are locked in — just hit Print each day.
          </p>

          {/* ── Saved reports ── */}
          {savedReports.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="saved-reports-list">
              <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Saved Reports</div>
              {savedReports.map((r) => {
                const count = (r.groups || []).reduce((sum, g) => sum + (hockey.by_group[g] || 0), 0);
                return (
                  <div key={r.id} className="flex items-center justify-between gap-2 p-3 rounded-lg border border-green-200 bg-green-50/50" data-testid={`saved-report-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-green-900 flex items-center gap-2 flex-wrap">
                        <span>{r.name}</span>
                        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700">{count} kids</Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                        Groups: {(r.groups || []).join(", ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-8"
                        disabled={downloading === r.id}
                        onClick={() => downloadPdf(`hockey-rink/saved/${r.id}/pdf`, {}, r.name, r.id)}
                        data-testid={`print-saved-${r.id}`}
                      >
                        <Printer className="w-3.5 h-3.5 mr-1" />
                        {downloading === r.id ? "..." : "Print"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600 hover:bg-rose-50 h-8 w-8 p-0"
                        onClick={() => handleDelete(r)}
                        data-testid={`delete-saved-${r.id}`}
                        title="Delete saved report"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Create new report ── */}
          {hockeyGroups.length === 0 ? (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 text-sm text-slate-600">
              {savedReports.length > 0
                ? "All available groups have been assigned to saved reports."
                : "No campers heading to the Hockey Rink today."}
            </div>
          ) : (
            <div className="mt-4 border-t pt-4">
              {!showCreate ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-green-300 text-green-700 hover:bg-green-50"
                  onClick={() => setShowCreate(true)}
                  data-testid="show-create-report"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create New Saved Report ({hockeyGroups.length} group{hockeyGroups.length !== 1 ? "s" : ""} available)
                </Button>
              ) : (
                <div className="space-y-3" data-testid="create-report-form">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">New Saved Report</div>
                    <div className="flex items-center gap-2 text-xs">
                      <button type="button" onClick={selectAllHockey}
                        className="px-2 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50"
                        data-testid="dismissal-hockey-select-all">Select all available</button>
                      <button type="button" onClick={clearHockey}
                        className="px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                        data-testid="dismissal-hockey-clear">Clear</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {hockeyGroups.map((g) => {
                      const checked = selectedHockeyGroups.includes(g);
                      const count = hockey.by_group[g] || 0;
                      return (
                        <label
                          key={g}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${checked ? "border-green-500 bg-green-50" : "border-slate-200 bg-white hover:border-green-300"}`}
                          data-testid={`dismissal-hockey-checkbox-${g}`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleGroup(g)} />
                          <span className="font-semibold text-sm">{g}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{count}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="text"
                      value={newReportName}
                      onChange={(e) => setNewReportName(e.target.value)}
                      placeholder="Report name (e.g. Report 1)"
                      className="flex-1 min-w-[160px] px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-400 outline-none"
                      data-testid="new-report-name"
                    />
                    <Button
                      onClick={handleSaveReport}
                      disabled={creating || !newReportName.trim() || selectedHockeyGroups.length === 0}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="save-report-btn"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {creating ? "Saving..." : `Save Report (${selectedHockeyGroups.length} groups)`}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setShowCreate(false); setSelectedHockeyGroups([]); setNewReportName(""); }}
                      disabled={creating}
                    >Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
